import React, { ReactNode } from 'react';
import { Grid, GridProps, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

interface GridTableProps {
  children: ReactNode;
}

interface GridTableCellBaseProps {
  children: ReactNode;
  width?: number | string;
  justify?:
    | 'flex-start'
    | 'center'
    | 'flex-end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
}

export const GridTable: React.FC<GridTableProps> = ({ children }) => {
  return (
    <Grid
      container
      direction="column"
      sx={{ border: '1px solid #F3F4F9', borderRadius: '12px', overflow: 'hidden' }}
    >
      {children}
    </Grid>
  );
};

export const GridTableHead: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Grid
      container
      sx={{
        borderBottom: '1px solid #F3F4F9',
      }}
    >
      {children}
    </Grid>
  );
};

export const GridTableHeaderCell: React.FC<GridTableCellBaseProps> = ({
  children,
  width,
  justify = 'flex-start',
}) => {
  return (
    <Grid
      item
      container
      justifyContent={justify}
      alignItems="center"
      sx={{
        flex: width ? `0 0 ${width}` : 1,
        maxWidth: width,
        padding: '14px 16px',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          fontFamily: 'Inter',
          whiteSpace: 'nowrap',
          fontSize: '14px',
          lineHeight: '146%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {children}
      </Typography>
    </Grid>
  );
};

export const GridTableBody: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Grid container direction="column">
      {children}
    </Grid>
  );
};

export const GridTableRow: React.FC<GridProps & { children: ReactNode }> = ({
  children,
  ...props
}) => {
  return (
    <Grid
      container
      sx={{
        borderBottom: '1px solid #F3F4F9',
        '&:last-child': { borderBottom: 'none' },
      }}
      {...props}
    >
      {children}
    </Grid>
  );
};

export const GridTableCell: React.FC<GridTableCellBaseProps> = ({
  children,
  width,
  justify = 'flex-start',
}) => {
  return (
    <Grid
      item
      container
      justifyContent={justify}
      alignItems="center"
      sx={{
        flex: width ? `0 0 ${width}` : 1,
        maxWidth: width,
        padding: '14px 16px',
        minWidth: 0,
      }}
    >
      {children}
    </Grid>
  );
};

interface GridTableTruncatedTypographyProps {
  children: ReactNode;
  sx?: SxProps<Theme>;
}

export const GridTableTruncatedTypography: React.FC<GridTableTruncatedTypographyProps> = ({
  children,
  sx,
}) => {
  return (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Inter',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '146%',
        width: '100%',
        ...sx,
      }}
      title={typeof children === 'string' ? children : undefined}
    >
      {children}
    </Typography>
  );
};
